import React, { useEffect, useState } from 'react';
import { Alert, Button, Modal, Spin, Tag, Typography } from 'antd';
import { TRANSLATION_LANGUAGES } from '@lingoping/core';
import type { AdminCard } from '../api';
import { errorMessage } from '../api';

const { Text } = Typography;

const GENDER_COLOR: Record<string, string> = {
  masculine: '#2563eb',
  feminine: '#dc2626',
  neuter: '#16a34a',
};
const POS_LABEL: Record<string, string> = {
  noun: 'Noun',
  verb: 'Verb',
  adjective: 'Adjective',
  adverb: 'Adverb',
  phrase: 'Phrase',
  other: 'Word',
};
const PERSONS = ['ich', 'du', 'er/sie/es', 'wir', 'ihr', 'sie/Sie'];

const sideStyle: React.CSSProperties = {
  flex: '1 1 300px',
  minWidth: 0,
  border: '1px solid #e2e4f5',
  borderRadius: 12,
  padding: 16,
  background: '#fff',
};
const labelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#8a8aa8',
  marginBottom: 10,
};

function pictureNote(card: AdminCard): string | null {
  if (card.part_of_speech !== 'noun' && card.part_of_speech !== 'verb') return null;
  if (card.image_status === 'failed')
    return 'The picture could not be made. Preparing the deck again retries it.';
  return 'The picture is still being made.';
}

const Front: React.FC<{ card: AdminCard }> = ({ card }) => {
  const color = (card.gender && GENDER_COLOR[card.gender]) || '#1e1b4b';
  return (
    <section style={sideStyle} aria-label="Front of the card">
      <div style={labelStyle}>Front</div>
      <Tag>{POS_LABEL[card.part_of_speech] ?? card.part_of_speech}</Tag>
      <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.25, marginTop: 10 }} lang="de">
        {card.article && <span style={{ color }}>{card.article} </span>}
        <span>{card.lemma}</span>
      </div>
      {card.ipa && <Text type="secondary">{card.ipa}</Text>}
      {card.plural && (
        <div style={{ marginTop: 8 }}>
          <Text type="secondary">Plural: </Text>
          <Text strong lang="de">
            {card.plural}
          </Text>
          {card.plural_ipa && (
            <Text type="secondary"> /{card.plural_ipa.replace(/^\/|\/$/g, '')}/</Text>
          )}
        </div>
      )}
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #eef0f6' }} lang="de">
        {card.example_de}
      </div>
    </section>
  );
};

const Back: React.FC<{ card: AdminCard }> = ({ card }) => {
  const note = pictureNote(card);
  const others = TRANSLATION_LANGUAGES.filter((l) => l.code !== 'en')
    .map((l) => ({ info: l, entry: card.translations[l.code as keyof typeof card.translations] }))
    .filter((t) => t.entry);
  return (
    <section style={sideStyle} aria-label="Back of the card">
      <div style={labelStyle}>Back</div>
      {card.image_url ? (
        <img
          src={card.image_url}
          alt={`Picture for ${card.lemma}`}
          style={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 8 }}
        />
      ) : (
        note && <Alert type="info" showIcon title={note} />
      )}
      <div style={{ fontSize: 20, fontWeight: 700, marginTop: 12 }}>{card.translation}</div>
      <div style={{ marginTop: 10, padding: '8px 12px', background: '#f5f6fc', borderRadius: 8 }}>
        <div style={{ fontWeight: 600 }} lang="de">
          {card.example_de}
        </div>
        <Text type="secondary">{card.example_en}</Text>
      </div>
      {card.verb && (
        <div style={{ marginTop: 12, fontSize: 13 }}>
          <Text strong>Verb forms</Text>
          <table style={{ width: '100%', marginTop: 4, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', fontWeight: 600, color: '#8a8aa8' }} />
                <th style={{ textAlign: 'left', fontWeight: 600, color: '#8a8aa8' }}>Present</th>
                <th style={{ textAlign: 'left', fontWeight: 600, color: '#8a8aa8' }}>Preterite</th>
              </tr>
            </thead>
            <tbody lang="de">
              {PERSONS.map((person, i) => (
                <tr key={person}>
                  <td style={{ color: '#8a8aa8', paddingRight: 8 }}>{person}</td>
                  <td>{card.verb!.present[i]}</td>
                  <td>{card.verb!.preterite[i]}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 4 }}>
            <Text type="secondary">Perfect: </Text>
            <span lang="de">
              {card.verb.auxiliary === 'sein' ? 'ist' : 'hat'} {card.verb.participle}
            </span>
            {card.verb.separable_prefix && (
              <Text type="secondary"> · separable prefix “{card.verb.separable_prefix}”</Text>
            )}
          </div>
        </div>
      )}
      <div style={{ marginTop: 14, fontSize: 13 }}>
        <Text strong>Other languages ({others.length} of 8)</Text>
        {others.length === 0 ? (
          <div>
            <Text type="secondary">None yet. They are added in the background.</Text>
          </div>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: '4px 0 0' }}>
            {others.map(({ info, entry }) => (
              <li key={info.code} style={{ display: 'flex', gap: 8, padding: '2px 0' }}>
                <span style={{ width: 84, flexShrink: 0, color: '#8a8aa8' }}>{info.name}</span>
                <span dir={info.dir} lang={info.code} style={{ flex: 1, minWidth: 0 }}>
                  {entry!.translation}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

/** What the preview shows: a name for the title and how to get the card. */
export interface CardSource {
  /** Stable per card, so opening another one starts fresh. */
  key: string;
  /** The word as the place it was opened from writes it. */
  lemma: string;
  load: () => Promise<AdminCard>;
}

/** Loads one card and shows it; remounted (by key) for every card, so it starts empty each time. */
const LoadedCard: React.FC<{ source: CardSource }> = ({ source }) => {
  const [card, setCard] = useState<AdminCard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { load, lemma } = source;

  useEffect(() => {
    let cancelled = false;
    load()
      .then((loaded) => !cancelled && setCard(loaded))
      .catch((e) => !cancelled && setError(errorMessage(e)));
    return () => {
      cancelled = true;
    };
    // `load` is a new function on every render of the caller; the key identifies the card.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source.key]);

  if (error) return <Alert type="error" showIcon title={error} />;
  if (!card) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin />
      </div>
    );
  }
  return (
    <>
      {card.lemma.toLowerCase() !== lemma.toLowerCase() && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          title={`The deck writes “${lemma}”; the card uses the dictionary form “${card.lemma}”.`}
        />
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-start' }}>
        <Front card={card} />
        <Back card={card} />
      </div>
    </>
  );
};

/**
 * Shows one prepared card, front and back side by side, as learners will get it. Read-only: the
 * card is loaded when the modal opens. `source` null closes it.
 */
export const CardPreviewModal: React.FC<{
  source: CardSource | null;
  onClose: () => void;
}> = ({ source, onClose }) => (
  <Modal
    open={!!source}
    title={source ? `Card for “${source.lemma}”` : ''}
    onCancel={onClose}
    footer={<Button onClick={onClose}>Close</Button>}
    width={760}
    destroyOnHidden
  >
    {source && <LoadedCard key={source.key} source={source} />}
  </Modal>
);
